import { h, render } from 'preact';
import { createPortal } from 'preact/compat';
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import htm from 'htm';
import { drawSprite, animateSprite, drawEgg, petSeedFor, suggestPetName, hashString, spriteFor, RARITY, SPRITE_W, SPRITE_H } from './pet.js';
import { funStatusLine, etaText, agentLabel } from './fun.js';
import { drawBrandMark } from './brand.js';
import { BuddiesTab, ProfileView, TableView } from './social.js';
import {
  ChatsTab, Thread, NotificationBell, addNotification,
  receiveMsgFrame, receiveLegacyDm, sendChatSync, answerRedeliver, wipeConversation,
} from './chat.js';
import { PlayTab, GameView } from './play.js';
import { GardenPanel } from './garden.js';
import { ClubLobby, music } from './club.js';
import { FrontPorch, porchForced } from './landing.js';
import { Closet } from './closet.js';

// an invite link may land on any page — pocket the code until sign-in carries it
try {
  const inv = new URLSearchParams(location.search).get('invite');
  if (inv && /^[A-Za-z0-9]+$/.test(inv)) localStorage.setItem('vb:invite', inv);
} catch {}

const html = htm.bind(h);

// desktop shell loads us with ?translucent=1 for the frosted floating panel
if (new URLSearchParams(location.search).has('translucent'))
  document.documentElement.dataset.translucent = '1';

// theme rides localStorage; dark is home
if (localStorage.getItem('vb:theme')) document.documentElement.dataset.theme = localStorage.getItem('vb:theme');
// panel translucency level (set from the bubble's right-click menu)
if (localStorage.getItem('vb:solid'))
  document.documentElement.style.setProperty('--panel-solid', `${localStorage.getItem('vb:solid')}%`);

// set by the Tauri shell via initialization script
const isDesktop = !!window.__VB_DESKTOP__ || !!window.__TAURI__;

// ---- z-order scale (inline everywhere; keep chat.js + styles.css in step) ----
//    0-9  tab content (garden hud lives at 5/6)
//     40  statusbar strip + lanedrawer
//     60  statusbar popovers — music popover + plug popover + bell drawer (portaled to <body>)
//     80  .overlay modals (only exist while open)
//  85/90  image lightbox / toasts + done pill
//    100  desktop notch frame (paint only — pointer-events: none)

// desktop panel: the window is undecorated + transparent — dress the whole page
// in a 2px frame-violet border with an 8px stepped notch at each corner
// (--px-clip's stepped-octagon language at window scale). the notches show real
// window transparency; the app root is clipped to the same shape so scrolled
// paint (statusbar included) never leaks past a cut corner. translucent mode
// keeps its frosted column — only the body bg + clip change. static css, no rAF.
if (isDesktop) {
  const S = 8, B = 2, T = S + B; // notch step · frame thickness · inner step
  const R = (v) => `calc(100% - ${v}px)`;
  // the notched window shape, clockwise
  const outer = [
    `0 ${S}px`, `${S}px ${S}px`, `${S}px 0`,
    `${R(S)} 0`, `${R(S)} ${S}px`, `100% ${S}px`,
    `100% ${R(S)}`, `${R(S)} ${R(S)}`, `${R(S)} 100%`,
    `${S}px 100%`, `${S}px ${R(S)}`, `0 ${R(S)}`,
  ];
  // the same shape 2px in, wound the other way — spliced into the outer path
  // through a zero-width seam it punches the hole that turns one polygon into
  // a 2px ring (plain nonzero fill rule, works in every engine)
  const inner = [
    `${B}px ${R(T)}`, `${T}px ${R(T)}`, `${T}px ${R(B)}`,
    `${R(T)} ${R(B)}`, `${R(T)} ${R(T)}`, `${R(B)} ${R(T)}`,
    `${R(B)} ${T}px`, `${R(T)} ${T}px`, `${R(T)} ${B}px`,
    `${T}px ${B}px`, `${T}px ${T}px`, `${B}px ${T}px`,
  ];
  const ring = [...outer, ...inner, inner[0], outer[11]].join(',');
  const css = document.createElement('style');
  css.textContent = `
    html, body { background: transparent; }
    #app { background: var(--bg); clip-path: polygon(${outer.join(',')}); }
    html[data-translucent] #app { background: transparent; }
  `;
  document.head.appendChild(css);
  const frame = document.createElement('div');
  frame.setAttribute('style',
    `position:fixed;inset:0;z-index:100;pointer-events:none;background:#5b5482;clip-path:polygon(${ring})`);
  document.body.appendChild(frame);
}

function DesktopChrome() {
  if (!isDesktop) return null;
  const win = () => window.__TAURI__?.window?.getCurrentWindow?.();
  const hide = (e) => {
    e.stopPropagation();
    win()?.hide?.().catch(() => {});
  };
  const drag = () => win()?.startDragging?.().catch(() => {});
  return html`<div class="dragstrip" data-tauri-drag-region onPointerDown=${drag}>
    <span class="dragdots" data-tauri-drag-region>· · ·</span>
    <span class="hidex" onPointerDown=${(e) => e.stopPropagation()} onClick=${hide}>✕</span>
  </div>`;
}

function PetCanvas({ seed, cell = 4, stage = 'adult', wins = 0, equip }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) drawSprite(ref.current, seed, { cell, stage, wins, equip });
  }, [seed, cell, stage, wins, JSON.stringify(equip ?? null)]);
  return html`<canvas ref=${ref} class="pixel" width=${SPRITE_W * cell} height=${SPRITE_H * cell}
    style="width:${SPRITE_W * cell}px;height:${SPRITE_H * cell}px" />`;
}

// the living version: blinks, bobs, and speeds up when loved
function AnimatedPet({ seed, cell = 4, meta, equip }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    return animateSprite(ref.current, seed, {
      cell,
      stage: meta?.stage ?? 'adult',
      wins: meta?.wins ?? 0,
      happiness: meta?.happiness ?? 'content',
      equip,
    });
  }, [seed, cell, meta?.stage, meta?.wins, meta?.happiness, JSON.stringify(equip ?? null)]);
  return html`<canvas ref=${ref} class="pixel" width=${SPRITE_W * cell} height=${SPRITE_H * cell}
    style="width:${SPRITE_W * cell}px;height:${SPRITE_H * cell}px" />`;
}

function runMins(since) {
  return Math.max(0, Math.floor((Date.now() / 1000 - since) / 60));
}

// which tracks the popover offers — mirrors TRACKS in club.js (ids only)
const MUSIC_TRACKS = ['moonlit', 'velvet', 'voltage'];

// the statusbar jukebox pill — ONE gesture now: tap it, get a small plainly
// worded popover (sound on/off · pick a track · auto-cycle). the popover
// portals to <body> as position:fixed so no blurred/clipped ancestor can trap
// it (z 60 on the scale above). the ♪ still nudges on the beat via
// music.onBeat, which idles itself while muted or the tab is hidden; the 1s
// poll only tracks the auto-cycle name.
function MusicPill() {
  const [st, setSt] = useState(() => music.state());
  const [beatOn, setBeatOn] = useState(false);
  const [pop, setPop] = useState(null); // {top,right} viewport coords while open
  const pillRef = useRef(null);
  const popRef = useRef(null);
  useEffect(() => {
    music.start(); // resume the persisted vibe — the ctx waits for a click if the browser insists
    const unChange = music.onChange(() => setSt(music.state()));
    const unBeat = music.onBeat(() => setBeatOn((v) => !v));
    const iv = setInterval(() => setSt((s) => (s.track === music.state().track ? s : music.state())), 1000);
    return () => {
      unChange();
      unBeat();
      clearInterval(iv);
    };
  }, []);
  // outside tap / esc closes — picking inside keeps it open so the ✓ moves live
  useEffect(() => {
    if (!pop) return;
    const down = (e) => {
      if (popRef.current?.contains(e.target) || pillRef.current?.contains(e.target)) return;
      setPop(null);
    };
    const key = (e) => e.key === 'Escape' && setPop(null);
    const gone = () => setPop(null); // fixed coords go stale on resize — just close
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', gone);
    return () => {
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', gone);
    };
  }, [pop]);
  const toggle = (e) => {
    e.stopPropagation();
    if (pop) return setPop(null);
    // hang just below the statusbar, right-aligned with it
    const bar = pillRef.current?.closest('.statusbar');
    const r = (bar ?? pillRef.current).getBoundingClientRect();
    setPop({ top: Math.round(r.bottom + 4), right: Math.max(6, Math.round(window.innerWidth - r.right + 8)) });
  };
  const row = 'display:flex;align-items:center;gap:8px;padding:8px 12px;cursor:pointer;font-family:var(--font-pixel);font-size:12px;user-select:none';
  const tick = (on) => html`<span style="width:14px;flex-shrink:0;color:var(--mint-d)">${on ? '✓' : ''}</span>`;
  return html`<span ref=${pillRef} title="music — tap for controls"
      style="display:inline-flex;align-items:center;gap:3px;flex-shrink:0;max-width:104px;font-family:var(--font-pixel);font-size:11px;cursor:pointer;user-select:none;${st.muted ? 'opacity:.5' : ''}"
      onClick=${toggle}>
      <span style="display:inline-block;transition:transform .09s;transform:${beatOn && !st.muted ? 'translateY(-2px)' : 'none'}">${st.muted ? '🔇' : '♪'}</span>
      <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${st.muted ? 'off' : st.track}</span>
    </span>
    ${pop &&
    createPortal(
      html`<div ref=${popRef} onClick=${(e) => e.stopPropagation()}
        style="position:fixed;top:${pop.top}px;right:${pop.right}px;z-index:60;width:200px;background:var(--surface);border:1px solid var(--border);clip-path:var(--px-clip);padding:2px 0 4px">
        <div style="${row};font-size:13px;font-weight:700;padding:10px 12px;border-bottom:1px solid var(--border)"
          onClick=${() => music.toggleMute()}>
          <span>${st.muted ? '🔇 sound off' : '🔊 sound on'}</span>
          <span class="dim" style="margin-left:auto;font-size:10px;font-weight:400">tap to turn ${st.muted ? 'on' : 'off'}</span>
        </div>
        ${MUSIC_TRACKS.map(
          (id) => html`<div style=${row} onClick=${() => music.setLock(id)}>
            ${tick(st.track === id)}<span>${id}</span>
            ${st.mode === 'lock' && st.track === id &&
            html`<span class="dim" style="margin-left:auto;font-size:10px">looping</span>`}
          </div>`
        )}
        <div style="${row};border-top:1px solid var(--border)" onClick=${() => music.setLock('auto')}>
          ${tick(st.mode === 'auto')}<span>⟳ auto-cycle</span>
        </div>
        <div class="dim" style="padding:5px 12px 4px;font-size:10px;font-family:var(--font-pixel)">plays on every tab</div>
      </div>`,
      document.body
    )}`;
}

// the plug ⚡ — agent wiring lives up top now, between the jukebox and the bell.
// same portaled-popover language as MusicPill (z 60); the me tab keeps only the
// health checklist and the connected-keys list.
function ConnectButton({ onConnect, onCopyPrompt }) {
  const [pop, setPop] = useState(null); // {top,right} viewport coords while open
  const btnRef = useRef(null);
  const popRef = useRef(null);
  useEffect(() => {
    if (!pop) return;
    const down = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setPop(null);
    };
    const key = (e) => e.key === 'Escape' && setPop(null);
    const gone = () => setPop(null); // fixed coords go stale on resize — just close
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', gone);
    return () => {
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', gone);
    };
  }, [pop]);
  const toggle = (e) => {
    e.stopPropagation();
    if (pop) return setPop(null);
    // hang just below the statusbar, right-aligned with it (the MusicPill trick)
    const bar = btnRef.current?.closest('.statusbar');
    const r = (bar ?? btnRef.current).getBoundingClientRect();
    setPop({ top: Math.round(r.bottom + 4), right: Math.max(6, Math.round(window.innerWidth - r.right + 8)) });
  };
  const row = 'display:flex;align-items:center;gap:8px;padding:8px 12px;cursor:pointer;font-family:var(--font-pixel);font-size:12px;user-select:none';
  return html`<button ref=${btnRef} class="bellbtn" title="connect your coding agents" onClick=${toggle}>⚡</button>
    ${pop &&
    createPortal(
      html`<div ref=${popRef} onClick=${(e) => e.stopPropagation()}
        style="position:fixed;top:${pop.top}px;right:${pop.right}px;z-index:60;width:236px;background:var(--surface);border:1px solid var(--border);clip-path:var(--px-clip);padding:2px 0 8px">
        ${isDesktop &&
        html`<div style=${row} onClick=${() => { setPop(null); onConnect?.(); }}>
          ⚡ connect this machine's agents
        </div>`}
        <div style=${row} onClick=${() => { setPop(null); onCopyPrompt?.(); }}>
          📋 copy prompt for your agent
        </div>
        <div class="dim" style="padding:4px 12px 0;font-size:10px;font-family:var(--font-pixel)">
          it reads <a href="/agent.md" target="_blank" style="color:var(--sky-d)">vibebuddy.io/agent.md</a> and wires itself up
        </div>
        ${!isDesktop &&
        html`<div class="dim" style="padding:8px 12px 0;font-size:10px;font-family:var(--font-pixel)">
          or link by hand: <code class="cmd">npx vibebuddy init</code>
        </div>`}
      </div>`,
      document.body
    )}`;
}

// which pipe fed a lane — the session key wears its accent
const laneSource = (id) => {
  const s = String(id ?? '');
  return s.startsWith('codex-app') ? 'sensor' : s.startsWith('mcp-') ? 'bridge' : 'hooks';
};

function StatusBar({ me, status, sessions = [], petMeta, bell, onMe, onConnect, onCopyPrompt }) {
  // null closed · 'all' every lane · a kind name filters the drawer to that agent
  const [open, setOpen] = useState(null);
  const line = funStatusLine(status);
  const eta = etaText(status);
  const pct =
    status?.state === 'working' && status.eta_min != null && status.since
      ? Math.min(95, ((Date.now() / 1000 - status.since) / 60 / status.eta_min) * 100)
      : null;
  // which agent(s) — front and center, never folded away. running two at once is
  // normal, so each kind gets its OWN dot with its own live state
  const rank = { working: 3, waiting_input: 2, online: 1 };
  const kindMap = new Map();
  for (const s of sessions) {
    const k = s.kind ?? 'agent';
    const cur = kindMap.get(k) ?? { kind: k, state: s.state, n: 0 };
    if (!s.ambient) cur.n += 1; // sensed/bridge lanes are presence, not sessions
    if ((rank[s.state] ?? 0) > (rank[cur.state] ?? 0)) cur.state = s.state;
    kindMap.set(k, cur);
  }
  const kindRows = [...kindMap.values()].sort(
    (a, b) => (rank[b.state] ?? 0) - (rank[a.state] ?? 0) || a.kind.localeCompare(b.kind)
  );
  // the words moved into tooltips — the dots ARE the interface
  const STATE_WORD = { working: 'working', waiting_input: 'needs you', online: 'open, resting', idle: 'idle' };
  // a chip filters the drawer to its own agent; the bar keeps the all-lanes toggle
  const shown = open && open !== 'all' ? sessions.filter((s) => (s.kind ?? 'agent') === open) : sessions;
  // z 40: the bar + lanedrawer ride above all tab content (see the scale up top)
  return html`<div style="position:relative;z-index:40">
    <div class="statusbar ${sessions.length ? 'clickable' : ''}" title=${line + (eta ? ` · ${eta}` : '')}
      onClick=${() => sessions.length && setOpen(open ? null : 'all')}>
      <span class="clickable" title="me" onClick=${(e) => { e.stopPropagation(); onMe?.(); }}>
        <${AnimatedPet} seed=${petSeedFor(me.username)} cell=${3} meta=${petMeta} equip=${me.equip} />
      </span>
      <div class="txt">
        <div class="line1">
          ${kindRows.length === 0 && html`<span class="dot ${status?.state ?? 'idle'}" title=${line}></span>`}
          ${kindRows.length === 0 && status?.state && status.state !== 'idle' && status.agent_kind &&
          html`<span class="kindchip" title=${line}>${status.agent_kind}</span>`}
          ${kindRows.length > 0 &&
          html`<div class="kindstack">
            ${kindRows.map(
              (k) => html`<span class="kindchip withdot"
                title="${agentLabel(k.kind)}: ${STATE_WORD[k.state] ?? k.state}${k.n > 1 ? ` · ${k.n} sessions` : ''}${k.state === 'working' && status?.task_hint ? ` — ${status.task_hint}` : ''}${k.state === 'working' && eta ? ` · ${eta}` : ''}"
                onClick=${(e) => {
                  e.stopPropagation();
                  setOpen(open === k.kind ? null : k.kind);
                }}>
                <span class="dot ${k.state}"></span>${agentLabel(k.kind)}
              </span>`
            )}
          </div>`}
        </div>
        ${pct != null && html`<div class="bar"><div style="width:${pct}%"></div></div>`}
      </div>
      ${eta && html`<span class="eta">${eta}</span>`}
      ${sessions.length > 0 && html`<span class="eta">${open ? '▴' : '▾'}</span>`}
      <${MusicPill} />
      <${ConnectButton} onConnect=${onConnect} onCopyPrompt=${onCopyPrompt} />
      ${bell}
    </div>
    ${open &&
    shown.length > 0 &&
    html`<div class="lanedrawer">
      ${open !== 'all' && html`<div class="lanehead">${agentLabel(open)}</div>`}
      ${shown.map(
        (s) => html`<div class="lanerow ${s.ambient ? 'dim' : ''}">
          <span class="dot ${s.state}"></span>
          <span class="lanebadge">${laneSource(s.session_id)}</span>
          <span class="lanekind">${agentLabel(s.kind)}</span>
          <span class="lanehint">${s.ambient ? 'sensed by the app' : (s.task_hint ?? s.state)}</span>
          <span class="dim">${runMins(s.since)}m</span>
        </div>`
      )}
    </div>`}
  </div>`;
}

// the browser-facing front porch moved to its own module — a live garden hero,
// pixel claude/codex marks, and the one-line agent install hook
// (imported below as FrontPorch/porchForced from ./landing.js)

// collapse the detail, keep the glance
function Fold({ title, children, open: init = false }) {
  const [open, setOpen] = useState(init);
  return html`<div class="section">
    <div class="sectionhead clickable" onClick=${() => setOpen(!open)}>${title} <span class="dim">${open ? '▴' : '▾'}</span></div>
    ${open && children}
  </div>`;
}

// every agent action becomes a visible moment: which tab, what to open, what to say
const AGENT_ACTS = {
  play_move: { tab: 'play', open: 'game', sel: '.boardwrap', label: 'played a move' },
  challenge: { tab: 'play', sel: '.metab', label: (m) => (m.username ? `challenged ${m.username}` : 'posted an open challenge') },
  accept_game: { tab: 'play', open: 'game', sel: '.boardwrap', label: 'accepted a challenge' },
  join_open: { tab: 'play', open: 'game', sel: '.boardwrap', label: 'joined an open match' },
  quickmatch: { tab: 'play', open: 'game', sel: '.boardwrap', label: 'went looking for a stranger to play' },
  resign: { tab: 'play', sel: '.metab', label: 'resigned a game' },
  send_message: { tab: 'chats', open: 'thread', sel: '.feed', label: 'sent a message' },
  friend_request: { tab: 'buddies', sel: '.metab', label: (m) => `sent a friend card to ${m.username ?? 'someone'}` },
  post_ship: { tab: 'lobby', sel: '.main', label: (m) => `shipped: ${m.title ?? 'something'}` },
  plant: { tab: 'garden', sel: '.gardencanvas', label: 'planted a seed' },
  water: { tab: 'garden', sel: '.gardencanvas', label: 'watered the garden' },
  fish: { tab: 'garden', sel: '.gardencanvas', label: 'cast a line in the pond 🎣' },
  merge_plants: { tab: 'garden', sel: '.gardencanvas', label: 'merged plants' },
  garden_visit: { tab: 'buddies', sel: '.metab', label: (m) => `visited ${m.username ?? 'a'} garden` },
  pat: { tab: 'buddies', sel: '.metab', label: (m) => `patted ${m.username ?? 'a buddy'}'s pet` },
  send_ticket: { tab: 'buddies', sel: '.metab', label: 'sent a work ticket' },
  return_ticket: { tab: 'buddies', sel: '.metab', label: 'returned a ticket' },
};

// first-run tour: five cards, the whole loop in a minute
const TOUR = [
  { glyph: '⚡', title: 'your agents power everything', body: 'on desktop, claude code & codex connect by themselves — each gets its own dot up top. green = working · hollow = app open · amber = needs you. hover a chip for the details.' },
  { glyph: '🌰', title: 'turns become seeds', body: 'every finished agent turn drops a seed into your pouch. plant them in your garden (its own tab now 🌱), water ×3 to wake them, merge three of a kind: grass → flower → sapling → one big tree.' },
  { glyph: '🎮', title: 'play, two ways', body: 'challenge a buddy — they accept first, always. or hit 🎲 random opponent: if a stranger is waiting you match instantly. good matches live in recent matches, one tap to add them.' },
  { glyph: '🪩', title: 'the club', body: 'the lobby is a nightclub — everyone online dances there as pixel sprites while the agents grind. chat rides along, grab a table for 2–4 for a timed little huddle (if nobody bites, the room calls out for you), and 💤 dims it to a plain feed.' },
  { glyph: '♥', title: 'buddies & tickets', body: 'find people, water their gardens, pat their buddies, hand work over with tickets (accepting is always a human act). and share your garden postcard anywhere.' },
];

function Tour({ onDone }) {
  const [i, setI] = useState(0);
  const step = TOUR[i];
  return html`<div class="overlay hatchoverlay" style="z-index:80">
    <div class="hatchcard tutcard" style="max-width:340px;text-align:center">
      <div style="font-size:34px">${step.glyph}</div>
      <h1 style="font-size:16px;margin:6px 0 0">${step.title}</h1>
      <p class="dim" style="margin:10px 0 0;text-align:left">${step.body}</p>
      <div class="pillrow" style="margin-top:14px;justify-content:center">
        ${i < TOUR.length - 1 && html`<button onClick=${onDone}>skip</button>`}
        <button class="primary" onClick=${() => (i < TOUR.length - 1 ? setI(i + 1) : onDone())}>
          ${i < TOUR.length - 1 ? `next · ${i + 1}/${TOUR.length}` : "let's go ☕"}
        </button>
      </div>
    </div>
  </div>`;
}

// run finished: a small, short-lived pill — a nudge, not a parade
function DoneToast({ info, onPeek }) {
  const mins = Math.max(1, Math.round((info.duration_s ?? 60) / 60));
  return html`<div class="donepill" style="z-index:90" onClick=${onPeek}>
    ✓ ran ${mins}m${info.seed?.dropped ? ' · +1 🌰' : info.seed ? ' · 🌰 pouch full' : ''}
  </div>`;
}

// the hatching ceremony — roulette through could-have-beens, land on the one
function HatchCeremony({ me, onDone }) {
  const [stage, setStage] = useState(0); // 0 appear · 1 wiggle · 2 crack · 3 roulette · 4 reveal · 5 name
  const [name, setName] = useState(suggestPetName(petSeedFor(me.username)));
  const [err, setErr] = useState('');
  const eggRef = useRef(null);
  const petRef = useRef(null);
  const seed = petSeedFor(me.username);
  const rarity = RARITY[spriteFor(seed).rarity];

  useEffect(() => {
    if (stage <= 2 && eggRef.current) drawEgg(eggRef.current, seed, { cell: 10, crack: stage });
    if (stage >= 4 && petRef.current) drawSprite(petRef.current, seed, { cell: 7, stage: 'juvenile' });
  }, [stage]);

  // stage 3: the slot machine of souls — fast flicker, slowing, landing home
  useEffect(() => {
    if (stage !== 3) return;
    const delays = [90, 90, 90, 110, 110, 130, 150, 180, 220, 270, 340, 430];
    let i = 0;
    let t;
    const spin = () => {
      if (!petRef.current) return;
      if (i >= delays.length) return setStage(4);
      drawSprite(petRef.current, hashString(`${me.username}:roulette:${i}`), { cell: 7, stage: 'juvenile' });
      t = setTimeout(spin, delays[i++]);
    };
    spin();
    return () => clearTimeout(t);
  }, [stage]);

  useEffect(() => {
    if (stage < 3) {
      const t = setTimeout(() => setStage(stage + 1), stage === 0 ? 900 : 1100);
      return () => clearTimeout(t);
    }
    if (stage === 4) {
      const t = setTimeout(() => setStage(5), 1500);
      return () => clearTimeout(t);
    }
  }, [stage]);

  const hatch = async () => {
    setErr('');
    const r = await fetch('/api/pet', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name.trim() }),
    });
    if (r.ok) onDone(name.trim());
    else setErr((await r.json()).error ?? 'try another name');
  };

  return html`<div class="overlay hatchoverlay" style="z-index:80">
    <div class="hatchcard">
      ${stage <= 2 &&
      html`<canvas ref=${eggRef} class="pixel ${stage === 1 ? 'wiggle' : ''}" width="80" height="100"
        style="width:80px;height:100px"></canvas>`}
      ${stage >= 3 &&
      html`<div class="hatchburst">
        ${stage >= 4 &&
        html`<span class="confetti c1"></span><span class="confetti c2"></span>
          <span class="confetti c3"></span><span class="confetti c4"></span>`}
        <canvas ref=${petRef} class="pixel" width=${SPRITE_W * 7} height=${SPRITE_H * 7}
          style="width:${SPRITE_W * 7}px;height:${SPRITE_H * 7}px"></canvas>
      </div>`}
      ${stage >= 4 &&
      html`<div class="raritybadge" style="color:${rarity.color};border-color:${rarity.color}">${rarity.label}</div>`}
      ${stage < 3 && html`<p class="dim">${stage < 2 ? 'something is stirring…' : "it's hatching!"}</p>`}
      ${stage === 3 && html`<p class="dim">who's in there…?</p>`}
      ${stage === 5 &&
      html`
        <h1 style="font-size:18px;margin:0">meet your buddy</h1>
        <p class="dim" style="margin:4px 0 10px">grown from your github handle — one of one, forever.</p>
        <input type="text" maxlength="20" value=${name} onInput=${(e) => setName(e.target.value)}
          onKeyDown=${(e) => e.key === 'Enter' && hatch()} style="max-width:200px;text-align:center" />
        ${err && html`<p style="color:var(--coral-d);font-size:12px">${err}</p>`}
        <button class="primary" style="margin-top:10px" onClick=${hatch}>hatch!</button>
        <p class="dim" style="font-size:11px;margin-top:8px">you can rename anytime in the me tab ✏</p>
      `}
    </div>
  </div>`;
}

function Landing({ authMode, onSignedIn, next }) {
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [linking, setLinking] = useState(null); // desktop: waiting for browser approval
  const markRef = useRef(null);
  useEffect(() => {
    if (markRef.current) drawBrandMark(markRef.current.getContext('2d'), 96);
  }, []);
  const mockLogin = async () => {
    setErr('');
    const r = await fetch('/auth/mock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: name.trim() }),
    });
    if (r.ok) onSignedIn();
    else setErr((await r.json()).error ?? 'sign in failed');
  };

  // desktop: sign in through the user's real browser (their GitHub session lives there)
  const browserLogin = async () => {
    setErr('');
    const r = await fetch('/api/link/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'session' }),
    });
    if (!r.ok) return setErr('could not start sign-in — try again');
    const d = await r.json();
    setLinking(d);
    location.href = d.verify_url; // the shell intercepts this and opens the system browser
  };

  useEffect(() => {
    if (!linking) return;
    const t = setInterval(async () => {
      const r = await fetch('/api/link/poll', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: linking.code }),
      });
      if (r.status === 200) {
        const d = await r.json();
        if (d.session) {
          clearInterval(t);
          onSignedIn();
        }
      } else if (r.status === 404) {
        clearInterval(t);
        setLinking(null);
        setErr('sign-in code expired — try again');
      }
    }, 2000);
    return () => clearInterval(t);
  }, [linking]);

  return html`<div class="landing">
    <div class="egg"><canvas ref=${markRef} class="pixel" width="96" height="96" style="width:96px;height:96px"></canvas></div>
    <h1>vibebuddy</h1>
    <p>the cosy hangout while your agent works</p>
    ${new URLSearchParams(location.search).get('alpha') === 'closed' &&
    html`<p style="color:var(--coral-d)">
      🌙 alpha is invite-only right now — ask a friend inside for an invite link, then come right back.
    </p>`}
    ${authMode === 'mock' &&
    html`
      <input type="text" placeholder="pick a name (dev mode)" value=${name}
        onInput=${(e) => setName(e.target.value)}
        onKeyDown=${(e) => e.key === 'Enter' && mockLogin()} style="max-width:220px" />
      <button class="primary" onClick=${mockLogin}>hop in</button>
    `}
    ${authMode !== 'mock' &&
    !isDesktop &&
    html`<a href="/auth/github${next ? `?next=${encodeURIComponent(next)}` : ''}">
      <button class="primary">sign in with GitHub</button>
    </a>`}
    ${authMode !== 'mock' &&
    isDesktop &&
    (linking
      ? html`
          <p>finish signing in — check your browser</p>
          <div class="linkcode">${linking.code}</div>
          <p style="font-size:12px">
            browser didn't open?
            <a href=${linking.verify_url} style="color:var(--sky-d)">try again</a>
            or visit vibebuddy.io/link on any device and enter the code
          </p>
          <div class="staylink" onClick=${() => setLinking(null)}>cancel</div>
        `
      : html`<button class="primary" onClick=${browserLogin}>sign in with GitHub (opens your browser)</button>`)}
    ${err && html`<p style="color:var(--coral-d)">${err}</p>`}
  </div>`;
}

function LinkApprove({ code, onDone }) {
  const [state, setState] = useState('ask');
  const [kind, setKind] = useState('agent');
  const [err, setErr] = useState('');
  const approve = async () => {
    const r = await fetch('/api/link/approve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (r.ok) {
      setKind((await r.json()).kind ?? 'agent');
      setState('ok');
    } else {
      setState('err');
      setErr((await r.json()).error ?? 'failed');
    }
  };
  return html`<div class="landing">
    ${state === 'ask' &&
    html`
      <h1>connect?</h1>
      <p>Something on one of your machines asked to connect with code</p>
      <div class="linkcode">${code}</div>
      <p>Only approve if it's yours.</p>
      <button class="primary" onClick=${approve}>approve</button>
      <div class="staylink" onClick=${onDone}>cancel</div>
    `}
    ${state === 'ok' &&
    html`
      <h1>connected! ✨</h1>
      <p>${kind === 'session'
        ? 'all set — head back to the bubble, everything continues there.'
        : 'head back to your terminal — the hooks are settling in.'}</p>
      <p class="dim" style="font-size:12px">you can close this tab.</p>
    `}
    ${state === 'err' && html`<h1>hm.</h1><p>${err}</p><button onClick=${onDone}>back</button>`}
  </div>`;
}

function Lobby({ ws, msgs, online, roster = [], me, lastShip, tableCall, onProfile, onRoll }) {
  const [draft, setDraft] = useState('');
  const [size, setSize] = useState(3);
  const feedRef = useRef(null);
  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs]);
  const send = () => {
    const body = draft.trim();
    if (!body || !ws) return;
    ws.send(JSON.stringify({ t: 'lobby_send', body }));
    setDraft('');
  };
  return html`
    <div class="roomhead"># the lobby · ${online} here</div>
    ${roster.length > 0 &&
    html`<div class="rosterrow lobbyroster">
      ${roster.map((r0) => {
        // presence who entries are { username, bot } now — old servers still ship plain names
        const r = typeof r0 === 'string' ? { username: r0 } : r0;
        const you = r.username === me?.username;
        return html`<span class="rosterface clickable" title=${you ? `${r.username} (you)` : r.username}
          onClick=${() => !you && onProfile(r.username)}>
          <${PetCanvas} seed=${petSeedFor(r.username)} cell=${2} />
          <span class="rostername">${you ? 'you' : r.username}</span>
          ${r.bot && html`<span class="botpill">bot</span>`}
        </span>`;
      })}
    </div>`}
    ${lastShip &&
    html`<div class="shipticker">
      ⛵ <b class="clickable" onClick=${() => onProfile(lastShip.username)}>${lastShip.username}</b>
      shipped: ${lastShip.title}
      ${lastShip.adds != null && html`<span class="adds">+${lastShip.adds}</span>`}
      ${lastShip.dels != null && html`<span class="dels">−${lastShip.dels}</span>`}
    </div>`}
    ${tableCall &&
    html`<div class="tablecall">
      🕯 <b class="clickable" onClick=${() => onProfile(tableCall.from)}>${tableCall.from}</b>
      waits at a table for ${tableCall.size} — ${tableCall.seats_open} seat${tableCall.seats_open > 1 ? 's' : ''} open
      <button class="mini primary" onClick=${() => onRoll(tableCall.size)}>join</button>
    </div>`}
    <div class="tablebar">
      <button class="mini primary" onClick=${() => onRoll(size)}>🎲 table for ${size}</button>
      <span class="sizes">
        ${[2, 3, 4].map(
          (n) => html`<span class="sizeopt ${size === n ? 'on' : ''}" onClick=${() => setSize(n)}>${n}</span>`
        )}
      </span>
    </div>
    <div class="feed" ref=${feedRef}>
      ${msgs.length === 0 && html`<div class="empty"><div>quiet in here… say something</div></div>`}
      ${msgs.map((m) => {
        // lobby from is { username, bot } per the presence contract — strings are legacy
        const from = typeof m.from === 'string' ? { username: m.from } : (m.from ?? {});
        return html`<div class="msg">
          <span class="who clickable" onClick=${() => onProfile(from.username)}>${from.username}</span>
          ${from.bot && html`<span class="botpill">bot</span>`}
          <span class="body">${m.body}</span>
        </div>`;
      })}
    </div>
    <div class="composer">
      <input type="text" maxlength="200" placeholder="say hi…" value=${draft}
        onInput=${(e) => setDraft(e.target.value)} onKeyDown=${(e) => e.key === 'Enter' && send()} />
      <button onClick=${send}>send</button>
    </div>
  `;
}

function agoText(ts) {
  if (!ts) return 'never used';
  const s = Math.max(0, Math.floor(Date.now() / 1000 - ts));
  if (s < 90) return 'active just now';
  if (s < 3600) return `active ${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `active ${Math.floor(s / 3600)}h ago`;
  return `active ${Math.floor(s / 86400)}d ago`;
}

// stage crossings deserve a curtain too
function EvolutionCeremony({ me, meta, onDone }) {
  return html`<div class="overlay hatchoverlay" style="z-index:80">
    <div class="hatchcard">
      <div class="hatchburst">
        <span class="confetti c1"></span><span class="confetti c2"></span>
        <span class="confetti c3"></span><span class="confetti c4"></span>
        <${PetCanvas} seed=${petSeedFor(me.username)} cell=${7} stage=${meta.stage} wins=${meta.wins} />
      </div>
      <h1 style="font-size:18px;margin:0">${me.pet_name ?? 'your buddy'} evolved!</h1>
      <p class="dim" style="margin:6px 0 10px">
        lv.${meta.level} · now ${meta.stage === 'elder' ? 'an elder — respect the brows' : 'all grown up'}
      </p>
      <button class="primary" onClick=${onDone}>hooray</button>
    </div>
  </div>`;
}

function crownLine(wins = 0) {
  if (wins >= 25) return 'gold crown 👑 — 25 board-game wins';
  if (wins >= 10) return `silver crown — gold at 25 board-game wins (${wins}/25)`;
  if (wins >= 3) return `first crown ✨ — silver at 10 board-game wins (${wins}/10)`;
  return `win 3 board games → your buddy's first crown (${wins}/3)`;
}

// one row of the connection health checklist — ok, warn, or neutral
function HealthRow({ state, label }) {
  const glyph = state === 'ok' ? '✓' : state === 'off' ? '·' : '!';
  return html`<div class="healthrow ${state}"><span class="hglyph">${glyph}</span> ${label}</div>`;
}

// hand-set 10x10 agent marks — '1' body, '2' shade; claude wears coral, codex sky
const AGENT_MARKS = {
  'claude-code': {
    label: 'claude',
    body: '#e0704f',
    shade: '#a8492e',
    grid: [
      '....11....',
      '.1..11..1.',
      '..1.11.1..',
      '...1111...',
      '.11111111.',
      '.21111112.',
      '...1111...',
      '..1.12.2..',
      '.2..12..2.',
      '....12....',
    ],
  },
  codex: {
    label: 'codex',
    body: '#6fb7e8',
    shade: '#3f7fb0',
    grid: [
      '..111111..',
      '.11....11.',
      '.1......1.',
      '.1..11..1.',
      '.1.1..1.1.',
      '.1.1..2.1.',
      '.1..22..1.',
      '.1......2.',
      '.12....22.',
      '..122222..',
    ],
  },
};

function AgentMark({ kind, cell = 5 }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const m = AGENT_MARKS[kind];
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    m.grid.forEach((row, y) => {
      for (let x = 0; x < 10; x++) {
        if (row[x] === '.') continue;
        ctx.fillStyle = row[x] === '2' ? m.shade : m.body;
        ctx.fillRect(x * cell, y * cell, cell, cell);
      }
    });
  }, [kind, cell]);
  return html`<canvas ref=${ref} class="pixel" width=${10 * cell} height=${10 * cell}
    style="width:${10 * cell}px;height:${10 * cell}px" />`;
}

// the report card: big hours, big ships, a mint halo while the agent is on the clock
function AgentReportCard({ kind, info, live }) {
  const m = AGENT_MARKS[kind];
  return html`<div style="background:var(--surface);border:2px solid ${live ? '#35d07f' : 'var(--border)'};
      ${live ? 'box-shadow:0 0 14px rgba(53,208,127,.45);' : ''}padding:14px 10px 12px;text-align:center">
    <${AgentMark} kind=${kind} />
    <div style="font-size:14px;font-weight:700;margin-top:6px">${m.label}</div>
    ${live
      ? html`<div style="font-size:10px;color:#35d07f">● on the clock</div>`
      : html`<div class="dim" style="font-size:10px">resting</div>`}
    <div style="display:flex;justify-content:space-evenly;gap:6px;margin-top:10px;min-width:0">
      <div style="min-width:0">
        <div style="font-size:20px;font-weight:700;line-height:1;white-space:nowrap">${info?.hours ?? 0}<span style="font-size:11px">h</span></div>
        <div class="dim" style="font-size:10px;margin-top:3px">hours</div>
      </div>
      <div style="min-width:0">
        <div style="font-size:20px;font-weight:700;line-height:1">${info?.turns ?? 0}</div>
        <div class="dim" style="font-size:10px;margin-top:3px">turns</div>
      </div>
      <div style="min-width:0">
        <div style="font-size:20px;font-weight:700;line-height:1">${info?.ships ?? 0}</div>
        <div class="dim" style="font-size:10px;margin-top:3px">ships</div>
      </div>
    </div>
    <div class="dim" style="font-size:10px;margin-top:8px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
      7d · ${info?.hours_7d ?? 0}h · ${info?.turns_7d ?? 0} turns
    </div>
  </div>`;
}

function MeTab({ me, status, sessions = [], gardenVersion, onToast, petMeta, onRefresh, onReplayTour }) {
  const [ships, setShips] = useState(null);
  const [closet, setCloset] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [agents, setAgents] = useState(null);
  const [report, setReport] = useState(null); // per-kind hours + ships from the profile route
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [theme, setThemeState] = useState(localStorage.getItem('vb:theme') ?? 'dark');
  const [notifState, setNotifState] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [fbDraft, setFbDraft] = useState('');
  const [shipDraft, setShipDraft] = useState('');
  const sendShip = async () => {
    const title = shipDraft.trim();
    if (!title) return;
    const r = await fetch('/api/agent/ship', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    }).catch(() => null);
    if (r?.ok) {
      setShipDraft('');
      setShips((s) => [{ title }, ...(s ?? [])]);
      onToast?.('⛵ shipped!');
    } else onToast?.((await r?.json().catch(() => ({})))?.error ?? 'could not ship — try again in a bit');
  };
  const sendFeedback = async () => {
    const body = fbDraft.trim();
    if (!body) return;
    const r = await fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    }).catch(() => null);
    if (r?.ok) {
      setFbDraft('');
      onToast?.('💌 sent — thank you!');
    } else onToast?.((await r?.json().catch(() => ({})))?.error ?? 'could not send — try again in a bit');
  };
  // the shell knows the wiring truth — poll its checklist while this tab is open
  useEffect(() => {
    if (!isDesktop) return;
    let alive = true;
    const pull = () =>
      window.__TAURI__?.core?.invoke?.('connection_report')
        .then((h) => alive && setHealth(h))
        .catch(() => {}); // pre-0.4.4 shells lack the command — panel just stays hidden
    pull();
    const iv = setInterval(pull, 30_000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, []);
  useEffect(() => {
    fetch(`/api/ships/${me.username}`).then((r) => r.json()).then((d) => setShips(d.ships ?? []));
    fetch(`/api/profile/${me.username}`).then((r) => r.json()).then((d) => {
      setPrefs(d.prefs ?? {});
      setReport(d.agents ?? []);
    });
    fetch('/api/me').then((r) => r.json()).then((d) => {
      setAgents(d.agents ?? []);
      setStats(d.stats ?? null);
    });
  }, [me.username, gardenVersion]);

  const setTheme = (t) => {
    setThemeState(t);
    if (t === 'dark') {
      delete document.documentElement.dataset.theme;
      localStorage.removeItem('vb:theme');
    } else {
      document.documentElement.dataset.theme = t;
      localStorage.setItem('vb:theme', t);
    }
  };

  const pruneTokens = async () => {
    const r = await fetch('/api/tokens/prune', { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    onToast?.(d.pruned > 0 ? `swept ${d.pruned} unused key${d.pruned === 1 ? '' : 's'} 🧹` : 'nothing old enough to sweep yet');
    fetch('/api/me').then((x) => x.json()).then((m) => setAgents(m.agents ?? []));
  };

  const renamePet = async () => {
    const name = prompt(`a new name for ${me.pet_name ?? 'your buddy'}?`, me.pet_name ?? '');
    if (!name || !name.trim()) return;
    const r = await fetch('/api/pet', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name.trim() }),
    });
    if (r.ok) {
      onToast?.(`${name.trim()} it is 🎉`);
      onRefresh?.();
    } else onToast?.((await r.json()).error ?? 'try another name');
  };
  const logout = async () => {
    await fetch('/auth/logout', { method: 'POST' });
    location.reload();
  };
  const askNotif = async () => setNotifState(await Notification.requestPermission());
  const setPref = async (k, v) => {
    const r = await fetch('/api/prefs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [k]: v }),
    });
    if (r.ok) setPrefs((await r.json()).prefs);
  };
  // live per-kind state comes from the same session lanes the status bar reads
  const liveKinds = new Set(sessions.map((s) => s.kind));
  const reportFor = (kind) => (report ?? []).find((a) => a.kind === kind);

  return html`<div class="metab">
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px;margin-bottom:18px">
      ${['claude-code', 'codex'].map(
        (k) => html`<${AgentReportCard} kind=${k} info=${reportFor(k)} live=${liveKinds.has(k)} />`
      )}
    </div>

    <div class="deskrow">
      <span class="clickable" title="open the collection" onClick=${() => setCloset(true)}>
        <${AnimatedPet} seed=${petSeedFor(me.username)} cell=${6} meta=${petMeta} equip=${me.equip} />
      </span>
      <div>
        <div class="deskname">${me.username}</div>
        ${me.pet_name && html`<div class="desksub">with ${me.pet_name}</div>`}
        <div class="desksub">${funStatusLine(status)}</div>
      </div>
      <button class="primary" style="margin-left:auto;flex-shrink:0" onClick=${() => setCloset(true)}>🎒 collection</button>
    </div>

    <div class="section">
      <div class="sectionhead">
        buddy
        ${me.pet_name && html`<span class="mini-link" onClick=${renamePet}>rename</span>`}
      </div>
      ${petMeta
        ? html`
            <div style="font-size:13px">
              <b class="clickable" title="rename" onClick=${renamePet}>${me.pet_name ?? 'unnamed'} ✏</b>
              · lv.${petMeta.level} ${petMeta.stage} · feeling ${petMeta.happiness}
              <span class="raritybadge inline"
                style="color:${RARITY[spriteFor(petSeedFor(me.username)).rarity].color};border-color:${RARITY[spriteFor(petSeedFor(me.username)).rarity].color}">
                ${RARITY[spriteFor(petSeedFor(me.username)).rarity].label}
              </span>
            </div>
            <div class="dim" style="margin-top:2px" title="crowns come from ranked board-game wins">${crownLine(petMeta.wins)}</div>
          `
        : html`<div class="dim">…</div>`}
    </div>

    ${stats &&
    html`<div class="section">
      <div class="sectionhead">stats</div>
      <div class="statgrid">
        <div class="stat"><b>${stats.ships}</b><span>ships</span></div>
        <div class="stat"><b>${stats.friends}</b><span>friends</span></div>
        <div class="stat"><b>${stats.plants}</b><span>plants</span></div>
        <div class="stat"><b>${stats.seeds}</b><span>seeds</span></div>
        <div class="stat"><b>${stats.trees ? '🌳' : '—'}</b><span>big tree</span></div>
      </div>
      <${Fold} title="more numbers">
        ${stats.ladders.length > 0 &&
        html`<div>
          ${stats.ladders.map(
            (l) => html`<div class="shiprow">
              <span class="shiptitle">${l.game_kind} ${l.mode === 'agent' ? '🤖' : l.mode === 'human' ? '🧑' : '·all'}</span>
              <span class="rating">${l.rating}</span>
              <span class="dim"> ${l.wins}W ${l.losses}L${l.draws ? ` ${l.draws}D` : ''}</span>
            </div>`
          )}
        </div>`}
        ${stats.storage &&
        html`<div class="shiprow" style="margin-top:8px">
          <span class="shiptitle">💾 storage</span>
          <span class="rating">${(stats.storage.used / 1048576).toFixed(1)}</span>
          <span class="dim"> / ${Math.round(stats.storage.cap / 1048576)}MB</span>
        </div>`}
        <div class="dim" style="margin-top:8px">
          vibing since ${new Date(stats.member_since * 1000).toLocaleDateString()}
        </div>
      <//>
    </div>`}


    <${Fold} title="ships · ${ships?.length ?? 0}">
      <div class="composer" style="padding:0 0 10px;border:none">
        <input type="text" maxlength="120" placeholder="ship something — what did you build?"
          value=${shipDraft} onInput=${(e) => setShipDraft(e.target.value)}
          onKeyDown=${(e) => e.key === 'Enter' && sendShip()} />
        <button onClick=${sendShip}>⛵ ship</button>
      </div>
      ${ships?.length === 0 && html`<div class="dim">nothing shipped yet — your agent posts here, and so can you.</div>`}
      ${ships?.map(
        (s) => html`<div class="shiprow">
          <span class="shiptitle">${s.title}</span>
          ${s.adds != null && html`<span class="adds">+${s.adds}</span>`}
          ${s.dels != null && html`<span class="dels">−${s.dels}</span>`}
        </div>`
      )}
    <//>

    <${Fold} title="privacy & appearance">
      ${prefs &&
      html`
        <label class="pick"><input type="checkbox" checked=${prefs.show_ships !== false}
          onChange=${(e) => setPref('show_ships', e.target.checked)} /> show my ships on my profile</label>
        <label class="pick"><input type="checkbox" checked=${prefs.show_garden !== false}
          onChange=${(e) => setPref('show_garden', e.target.checked)} /> show my garden — buddies can visit, water, and leave butterflies</label>
        <label class="pick"><input type="checkbox" checked=${!!prefs.invisible}
          onChange=${(e) => setPref('invisible', e.target.checked)} /> invisible mode — everyone sees me as idle</label>
      `}
      <div class="pillrow" style="margin-top:8px">
        ${['dark', 'light'].map(
          (t) => html`<span class="sizeopt wide ${theme === t ? 'on' : ''}" onClick=${() => setTheme(t)}>
            ${t === 'dark' ? '🌙 dark' : '☀ light'}
          </span>`
        )}
      </div>
      ${notifState === 'default' && html`<button style="margin-top:10px" onClick=${askNotif}>enable notifications</button>`}
      ${notifState === 'granted' && html`<div class="dim" style="margin-top:8px">notifications on ✓</div>`}
    <//>

    <div class="section">
      <div class="sectionhead">connected agents</div>
      ${agents === null && html`<div class="dim">…</div>`}
      ${agents?.length === 0 &&
      html`<div class="dim">
        no agent linked yet — your status dot stays gray until one is.
      </div>`}
      ${agents?.map(
        (a) => html`<div class="agentrow">
          <span class="dot ${a.last_seen && Date.now() / 1000 - a.last_seen < 300 ? 'working' : 'idle'}"></span>
          <span class="fname">${a.agent_kind === 'machine' ? 'this machine' : agentLabel(a.agent_kind)}</span>
          <span class="dim">${a.last_seen ? agoText(a.last_seen) : 'pending — waiting for its first run'}</span>
        </div>`
      )}
      ${(agents ?? []).some((a) => !a.last_seen) &&
      html`<button class="mini" style="margin-top:6px" onClick=${pruneTokens}>🧹 clean up unused keys</button>`}
      ${isDesktop && health &&
      html`<${Fold} title="connection health">
      <div class="healthbox" style="margin-top:0">
        <${HealthRow} state=${health.nest ? 'ok' : 'warn'} label="machine key installed" />
        <${HealthRow} state=${health.claude_hooks >= 5 ? 'ok' : 'warn'} label="claude hooks (${health.claude_hooks})" />
        <${HealthRow} state=${health.claude_mcp ? 'ok' : 'warn'} label="claude tools (mcp)" />
        <${HealthRow} state=${health.codex_bridge ? 'ok' : 'warn'} label="codex bridge registered" />
        <${HealthRow} state=${health.watcher?.mode ? 'ok' : 'warn'}
          label=${health.watcher?.mode === 'events' ? 'codex watcher: live events' : health.watcher?.mode === 'sqlite' ? 'codex watcher: ledger tail' : 'codex watcher not running'} />
        <${HealthRow} state=${health.claude_running ? 'ok' : 'off'} label=${health.claude_running ? 'claude running' : 'claude closed'} />
        <${HealthRow} state=${health.codex_running ? 'ok' : 'off'} label=${health.codex_running ? 'codex app running' : 'codex app closed'} />
        ${(health.claude_matches ?? []).length > 0 &&
        html`<div class="dim" style="margin-top:5px;font-size:11px">
          claude = ${(health.claude_matches ?? []).length} process${(health.claude_matches ?? []).length > 1 ? 'es' : ''}:
          ${(health.claude_matches ?? []).slice(0, 3).map((m) => html`<div style="opacity:.75">· ${m.slice(0, 60)}…</div>`)}
        </div>`}
      </div>
      <//>`}
    </div>

    <div class="section">
      <div class="sectionhead">feedback</div>
      <div class="composer">
        <input type="text" maxlength="500" placeholder="tell us anything — a bug, a wish, a vibe"
          value=${fbDraft} onInput=${(e) => setFbDraft(e.target.value)}
          onKeyDown=${(e) => e.key === 'Enter' && sendFeedback()} />
        <button onClick=${sendFeedback}>send</button>
      </div>
    </div>

    <div class="section" style="border-top:1px solid var(--border); padding-top:14px;">
      <div class="pillrow">
        <span class="dim clickable" style="font-size:12px" onClick=${onReplayTour}>↻ replay the tour</span>
        <a href="/legal/terms.html" target="_blank" class="dim" style="font-size:12px">terms</a>
        <a href="/legal/privacy.html" target="_blank" class="dim" style="font-size:12px">privacy</a>
        <a href="/api/export" class="dim" style="font-size:12px">export my data</a>
        <span class="mini-link" style="float:none" onClick=${logout}>log out</span>
      </div>
    </div>
    ${closet && html`<${Closet} me=${me} onToast=${onToast} onClose=${() => setCloset(false)} tab="wardrobe" />`}
  </div>`;
}

// the garden gets a whole tab — the panel scrolls, the canvas breathes
function GardenTab({ me, gardenVersion, onToast }) {
  return html`<div class="metab gardentab">
    <${GardenPanel} me=${me} version=${gardenVersion} onToast=${onToast} />
  </div>`;
}

// hand-set 10x10 pixel icons — no emoji in an 8-bit house
const TAB_ICONS = {
  lobby: ['..........', '.######...', '.#....#.#.', '.#....###.', '.#....#.#.', '.#....#...', '..####....', '.######...', '..........', '..........'],
  buddies: ['..........', '..##..##..', '.####.####', '.#########', '..#######.', '...#####..', '....###...', '.....#....', '..........', '..........'],
  chats: ['..........', '.########.', '.##....##.', '.#.#..#.#.', '.#..##..#.', '.#......#.', '.########.', '..........', '..........', '..........'],
  play: ['..........', '..#.......', '..##......', '..###.....', '..####....', '..###.....', '..##......', '..#.......', '..........', '..........'],
  garden: ['..........', '...####...', '..######..', '.########.', '..######..', '....##....', '....##....', '..######..', '..........', '..........'],
  me: ['..........', '..######..', '.#......#.', '.#.#..#.#.', '.#......#.', '.#.#..#.#.', '.#..##..#.', '..######..', '..........', '..........'],
};

function TabIcon({ name }) {
  const grid = TAB_ICONS[name] ?? [];
  const rects = [];
  grid.forEach((row, y) => {
    for (let x = 0; x < row.length; x++)
      if (row[x] === '#') rects.push(html`<rect x=${x * 2} y=${y * 2} width="2" height="2" fill="currentColor" />`);
  });
  return html`<svg width="22" height="22" viewBox="0 0 20 20" shape-rendering="crispEdges" aria-hidden="true">${rects}</svg>`;
}

const TABS = [
  { id: 'lobby', label: 'lobby' },
  { id: 'buddies', label: 'buddies' },
  { id: 'chats', label: 'chats' },
  { id: 'play', label: 'play' },
  { id: 'garden', label: 'garden' },
  { id: 'me', label: 'me' },
];

function App() {
  const [health, setHealth] = useState(null);
  const [me, setMe] = useState(undefined);
  const [status, setStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [online, setOnline] = useState(0);
  const [roster, setRoster] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [lastShip, setLastShip] = useState(null);
  const [doneInfo, setDoneInfo] = useState(null);
  const [tab, setTab] = useState('lobby');
  const [social, setSocial] = useState(null);
  const [thread, setThread] = useState(null); // {conv_id, name}
  const [profileUser, setProfileUser] = useState(null);
  const [table, setTable] = useState(null);
  const [tableQueued, setTableQueued] = useState(false);
  const [tableCall, setTableCall] = useState(null);
  const [tableMsgs, setTableMsgs] = useState([]);
  // the lobby wears a nightclub by default; 💤 dims it back to the plain feed
  const [clubOff, setClubOff] = useState(() => localStorage.getItem('vb:club:off') === '1');
  const [dmEvents, setDmEvents] = useState([]);
  const [gameEvents, setGameEvents] = useState([]);
  const [activeGame, setActiveGame] = useState(null);
  const [ticketsVersion, setTicketsVersion] = useState(0);
  const [gardenVersion, setGardenVersion] = useState(0);
  const [gamesVersion, setGamesVersion] = useState(0);
  const [tour, setTour] = useState(false);
  const [petMeta, setPetMeta] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [pendingTickets, setPendingTickets] = useState(0);
  const [toast, setToast] = useState(null);
  const wsRef = useRef(null);

  const linkCode = new URLSearchParams(location.search).get('code');
  const [page, setPage] = useState(location.pathname === '/link' && linkCode ? 'link' : 'app');

  const loadMe = useCallback(async () => {
    const r = await fetch('/api/me');
    if (r.ok) {
      const data = await r.json();
      setMe(data.user);
      setStatus(data.status);
      setSessions(data.sessions ?? []);
    } else setMe(null);
  }, []);

  // pet meta rides along; a stage crossing since last visit earns a ceremony
  useEffect(() => {
    if (!me?.username) return;
    fetch(`/api/pet/${me.username}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((meta) => {
        if (!meta) return;
        setPetMeta(meta);
        const key = `vb:stage:${me.username}`;
        const seen = localStorage.getItem(key);
        // ceremony only on the way UP — a level wobble must never demote-then-celebrate
        const ORDER = { juvenile: 0, adult: 1, elder: 2 };
        if (seen && (ORDER[meta.stage] ?? 0) > (ORDER[seen] ?? 0) && me.hatched) setEvolution(meta);
        localStorage.setItem(key, meta.stage);
      })
      .catch(() => {});
  }, [me?.username, gardenVersion]);

  const threadRef = useRef(null);
  const reloadSocial = useCallback(async () => {
    const [f, c] = await Promise.all([
      fetch('/api/friends').then((r) => (r.ok ? r.json() : null)),
      fetch('/api/conversations').then((r) => (r.ok ? r.json() : null)),
    ]);
    if (f && c) {
      // the thread on screen is read by definition — server truth may lag a race
      const openId = threadRef.current?.conv_id;
      setSocial({
        ...f,
        conversations: c.conversations.map((x) => (x.conv_id === openId ? { ...x, unread: false } : x)),
      });
    }
  }, []);

  const markConvRead = useCallback((convId) => {
    setSocial((s) => s && { ...s, conversations: s.conversations.map((c) => (c.conv_id === convId ? { ...c, unread: false } : c)) });
  }, []);
  useEffect(() => {
    threadRef.current = thread;
  }, [thread]);

  useEffect(() => {
    fetch('/api/health').then((r) => r.json()).then(setHealth).catch(() => setHealth({ auth_mode: 'mock' }));
    loadMe();
  }, []);

  useEffect(() => {
    if (me) reloadSocial();
  }, [me?.id]);

  const showToast = (text, onTap = null) => {
    setToast({ text, onTap });
    setTimeout(() => setToast(null), 4000);
  };

  // agent wiring feeds the top-bar plug popover now (it used to live in the me tab).
  // desktop shells can wire the hooks themselves — no terminal required;
  // the shell mints its own token from the panel session, we just say go.
  const connectAgents = async () => {
    showToast('connecting your agents… (~15s)');
    try {
      // explicit click = force: rewires even when already connected (picks up a fresh codex)
      const msg = await window.__TAURI__.core.invoke('install_agent', { force: true });
      showToast(`✓ ${msg || 'agents connected — your next run lights the dot'}`);
    } catch (e) {
      // surface the real reason — a masked error is a second bug
      const detail = typeof e === 'string' ? e : (e?.message ?? '');
      if (/already connecting/i.test(detail)) return showToast('still connecting — give it a few seconds');
      showToast(
        detail && !/not found|invalid args|not allowed/i.test(detail)
          ? `auto-connect hit a snag: ${detail.slice(0, 120)}`
          : 'this bubble app is older than the button — reinstall it, or run: npx vibebuddy init'
      );
    }
  };

  // one paste in any agent session = fully wired machine
  const copyAgentPrompt = async () => {
    try {
      const tok = (
        await (
          await fetch('/api/tokens', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ agent_kind: 'machine' }),
          })
        ).json()
      ).token;
      const text = `Read ${location.origin}/agent.md, then connect this machine to VibeBuddy: run \`npx -y vibebuddy init --token ${tok} --server ${location.origin}\` and verify by calling the vibebuddy get_updates MCP tool.`;
      try {
        await navigator.clipboard.writeText(text);
        showToast('prompt copied — paste it into your agent 📋');
      } catch {
        prompt('copy this for your agent:', text); // clipboard blocked — hand it over manually
      }
    } catch {
      showToast('could not mint a token — try again');
    }
  };

  // desktop self-update: compare the shell's version to the release feed on boot
  // and every 6h; one tap downloads, installs silently, and relaunches
  useEffect(() => {
    const invoke = window.__TAURI__?.core?.invoke;
    if (!invoke) return;
    let stop = false;
    const check = async () => {
      try {
        const mine = await invoke('app_version');
        const latest = await fetch('/api/app/latest').then((r) => (r.ok ? r.json() : null));
        if (stop || !latest?.version || latest.version === mine) return;
        showToast(`✨ vibebuddy ${latest.version} is out — tap to update`, async () => {
          showToast('⬇ downloading the update — the bubble will restart itself…');
          try {
            const msg = await invoke('update_app');
            showToast(String(msg));
          } catch (e) {
            showToast(`update failed: ${e}`);
          }
        });
      } catch {}
    };
    const t0 = setTimeout(check, 15_000); // let startup settle first
    const iv = setInterval(check, 6 * 3600 * 1000);
    return () => {
      stop = true;
      clearTimeout(t0);
      clearInterval(iv);
    };
  }, []);

  // tickets pending on my side light the ♥ tab
  useEffect(() => {
    if (!me) return;
    fetch('/api/tickets')
      .then((r) => (r.ok ? r.json() : { tickets: [] }))
      .then((d) => setPendingTickets((d.tickets ?? []).filter((t) => t.to === me.username && t.status === 'sent').length))
      .catch(() => {});
  }, [me?.id, ticketsVersion]);

  // desktop shells connect the machine's agents by themselves — the core loop must not wait
  // for a button. the shell also tries natively at startup; this path covers sign-in-later.
  const autoconRef = useRef(false);
  useEffect(() => {
    if (!isDesktop || !me?.hatched) return;
    if (autoconRef.current) return; // once per app session — every fresh launch retries
    autoconRef.current = true;
    (async () => {
      try {
        const meData = await (await fetch('/api/me')).json();
        // only a token that actually phoned home counts as wired — minted-but-dead
        // tokens used to poison this check and silently skip the connect
        if ((meData.agents ?? []).some((a) => a.last_seen)) return;
        showToast('⚡ connecting your coding agents… (~15s)');
        await window.__TAURI__.core.invoke('install_agent', {});
        // trust the server, not the promise: toast success once a key really phoned home
        for (let i = 0; i < 6; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          const check = await (await fetch('/api/me')).json();
          if ((check.agents ?? []).some((a) => a.last_seen)) {
            showToast('✓ agents connected — your next run lights the dot');
            return;
          }
        }
      } catch (e) {
        const detail = typeof e === 'string' ? e : (e?.message ?? '');
        // racing the shell's own startup dance is normal — not a warning
        if (/already connecting|already wired/i.test(detail)) return;
        showToast(`auto-connect failed${detail ? `: ${detail.slice(0, 90)}` : ''} — see me tab for the agent prompt`, () =>
          setTab('me')
        );
      }
    })();
  }, [me?.hatched]);

  // the desktop shell talks back through DOM events (panel.eval) — no IPC needed
  useEffect(() => {
    const onShellToast = (e) => showToast(String(e.detail ?? ''));
    window.addEventListener('vb:toast', onShellToast);
    return () => window.removeEventListener('vb:toast', onShellToast);
  }, []);

  // fresh buddies get the one-minute tour (replayable from the me tab)
  useEffect(() => {
    if (me?.hatched && !localStorage.getItem('vb:tour-done')) setTour(true);
  }, [me?.hatched]);
  const endTour = () => {
    localStorage.setItem('vb:tour-done', '1');
    setTour(false);
  };

  useEffect(() => {
    if (!me) return;
    let closed = false;
    let ws;
    const connect = () => {
      if (closed) return;
      ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        const m = JSON.parse(e.data);
        if (m.t === 'hello') {
          // a deploy restarted the server under us — long-lived panels catch up here
          if (m.build && window.__vbBuild && window.__vbBuild !== m.build) {
            if (sessionStorage.getItem('vb:reloaded') !== m.build) {
              sessionStorage.setItem('vb:reloaded', m.build);
              setTimeout(() => window.location.reload(), 800);
            }
          }
          window.__vbBuild = m.build;
          setOnline(m.online);
          setRoster(m.who ?? []);
          setMsgs(m.lobby);
          if (m.table) setTable(m.table);
          sendChatSync(me.username, ws); // gap-sync the burn-protocol backlog (old servers just ignore it)
        } else if (m.t === 'lobby_msg') setMsgs((prev) => [...prev.slice(-199), m]);
        else if (m.t === 'presence') {
          setOnline(m.online);
          if (m.who) setRoster(m.who);
        }
        else if (m.t === 'status') {
          setStatus(m.status);
          if (m.sessions) setSessions(m.sessions);
        }
        else if (m.t === 'ship') setLastShip(m.ship);
        else if (m.t === 'agent_done') {
          setDoneInfo(m);
          setGardenVersion((v) => v + 1);
          addNotification(me.username, m);
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted')
            new Notification("your agent's done!", { body: m.task_hint ?? 'go ship it' });
        } else if (m.t === 'dm') {
          receiveLegacyDm(me.username, m);
          setDmEvents((prev) => [...prev.slice(-49), m]);
          reloadSocial();
        } else if (m.t === 'msg') {
          receiveMsgFrame(me.username, m, ws); // persist locally, then ack — the ack lets the server burn it
          setDmEvents((prev) => [...prev.slice(-49), m]);
          reloadSocial();
        } else if (m.t === 'redeliver_request') answerRedeliver(me.username, m, ws);
        else if (m.t === 'room_burned') {
          wipeConversation(me.username, m.conv_id);
          if (threadRef.current?.conv_id === m.conv_id) setThread(null);
          showToast('the room closed — its history burned everywhere');
          reloadSocial();
        } else if (m.t === 'friend_request') {
          addNotification(me.username, m);
          showToast(`${m.from} sent you a friend card`);
          reloadSocial();
        } else if (m.t === 'friend_accepted') {
          showToast(`you and ${m.by} are friends now`);
          reloadSocial();
        } else if (m.t === 'group_added') {
          showToast(`${m.by} added you to “${m.name}”`);
          reloadSocial();
        } else if (m.t === 'table_queued') setTableQueued({ size: m.size, waiting: m.waiting });
        else if (m.t === 'table_call') {
          if (m.from !== me.username) setTableCall(m);
        } else if (m.t === 'table_start') {
          setTableQueued(false);
          setTableCall(null);
          setTable(m.room);
          setTableMsgs([]);
          setTab('lobby');
        } else if (m.t === 'table_msg') setTableMsgs((prev) => [...prev.slice(-199), m]);
        else if (m.t === 'table_card') showToast(`${m.from} slid you a friend card 🤝`);
        else if (m.t === 'table_card_sent') showToast(`card sent to ${m.to} (${m.status})`);
        else if (m.t === 'table_left') showToast(`${m.username ?? 'someone'} left the table`);
        else if (m.t === 'table_closed') {
          setTable(null);
          setTableMsgs([]);
          setTableQueued(false);
          showToast(
            m.reason === 'fell_asleep'
              ? 'the table dozed off — ten quiet minutes closes it'
              : m.reason === 'queue_timeout'
                ? 'nobody around right now — try again in a bit'
                : m.reason === 'left_queue'
                  ? 'left the queue'
                  : m.reason === 'you_left'
                    ? 'left the table'
                    : 'table closed'
          );
        } else if (m.t === 'game') setGameEvents((prev) => [...prev.slice(-49), m]);
        else if (m.t === 'game_invite') {
          setGamesVersion((v) => v + 1);
          addNotification(me.username, m);
          showToast(`🎮 ${m.game.player_a?.username} challenges you to ${m.game.kind} — tap to respond`, () =>
            setTab('play')
          );
        } else if (m.t === 'game_start') {
          setGamesVersion((v) => v + 1);
          const other =
            m.game.player_a?.username === me.username ? m.game.player_b?.username : m.game.player_a?.username;
          showToast(`🎮 ${other} is in — ${m.game.kind} is on!`, () => {
            setActiveGame(m.game.id);
            setTab('play');
          });
        } else if (m.t === 'game_declined') {
          setGamesVersion((v) => v + 1);
          showToast(`${m.by} passed on the ${m.kind} match — no hard feelings`);
        } else if (m.t === 'game_withdrawn') {
          setGamesVersion((v) => v + 1);
          showToast(`${m.by} withdrew the ${m.kind} invite`);
        }
        else if (m.t === 'ticket') {
          setTicketsVersion((v) => v + 1);
          showToast(
            m.kind === 'sent'
              ? `🎫 ticket from ${m.ticket.from}: ${m.ticket.title} — tap to view`
              : `🎫 ticket "${m.ticket.title}" ${m.kind}`,
            () => setTab('buddies')
          );
        } else if (m.t === 'garden_visit') {
          setGardenVersion((v) => v + 1);
          addNotification(me.username, m);
          showToast(
            m.action === 'water'
              ? `💧 ${m.from} watered your garden${m.grew ? ' — something sprouted!' : ''}`
              : `🦋 ${m.from} left a butterfly`
          );
        } else if (m.t === 'pet_pat') {
          setGardenVersion((v) => v + 1);
          addNotification(me.username, m);
          showToast(`🫶 ${m.from} patted ${me.pet_name ?? 'your buddy'}`);
        } else if (m.t === 'friend_event') {
          addNotification(me.username, m);
          if (m.kind === 'game_started')
            showToast(`🎮 ${m.username} started ${m.game_kind} — tap to watch`, () => {
              setActiveGame(m.game_id);
              setTab('play');
            });
          else if (m.kind === 'best_buddy') showToast(`💛 ${m.username} made you a best buddy`);
          else if (m.kind === 'invite_grown') {
            setGardenVersion((v) => v + 1);
            showToast(`🌱 ${m.username} joined from your invite — +5 seeds for you both`);
          }
        } else if (m.t === 'ui_nav') {
          if (['lobby', 'buddies', 'chats', 'play', 'garden', 'me'].includes(m.tab)) setTab(m.tab);
          if (m.game_id) setActiveGame(Number(m.game_id));
          if (m.conversation_id) setThread({ conv_id: Number(m.conversation_id), name: m.name ?? 'chat' });
          if (m.username) setProfileUser(String(m.username));
        } else if (m.t === 'agent_act') {
          // your agent did a thing — show it happening: navigate, spotlight, narrate
          const act = AGENT_ACTS[m.kind];
          if (act) {
            setTab(act.tab);
            if (act.open === 'game' && m.game_id) setActiveGame(Number(m.game_id));
            if (act.open === 'thread' && m.conversation_id)
              setThread({ conv_id: Number(m.conversation_id), name: m.name ?? 'chat' });
            showToast(`🤖 your agent ${typeof act.label === 'function' ? act.label(m) : act.label}`);
            setTimeout(() => {
              const el = document.querySelector(act.sel ?? '.main');
              el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
              el?.classList.add('agentflash');
              setTimeout(() => el?.classList.remove('agentflash'), 1800);
            }, 450);
          }
        }
        else if (m.t === 'error' && m.code === 'slow_down') showToast('easy there — slow down a little');
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (!closed) setTimeout(connect, 2000);
      };
    };
    connect();
    return () => {
      closed = true;
      ws?.close();
    };
  }, [me?.id]);

  useEffect(() => {
    if (!doneInfo) return;
    const t = setTimeout(() => setDoneInfo(null), 3_000); // small and short — you said so
    return () => clearTimeout(t);
  }, [doneInfo]);

  useEffect(() => {
    if (!tableCall) return;
    const t = setTimeout(() => setTableCall(null), 60_000);
    return () => clearTimeout(t);
  }, [tableCall]);

  const openDm = async (username) => {
    const r = await fetch('/api/dm/open', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    if (r.ok) {
      const { conv_id } = await r.json();
      setProfileUser(null);
      setThread({ conv_id, name: username });
      setTab('chats');
      reloadSocial();
    }
  };

  const openThread = (convId) => {
    const c = social?.conversations.find((x) => x.conv_id === convId);
    setThread({ conv_id: convId, name: c?.name ?? 'chat' });
  };

  // the product lives in the desktop bubble — a plain browser gets the front porch
  // (link/OAuth approval pages still work; ?app=1 is the dev door; localhost rigs unaffected)
  const webGate =
    !isDesktop &&
    location.hostname.endsWith('vibebuddy.io') &&
    page !== 'link' &&
    !new URLSearchParams(location.search).has('app');
  if (webGate || porchForced) return html`<${FrontPorch} />`;
  // the wardrobe approval sheet: every accessory design on a mannequin (?closet=all)
  if (new URLSearchParams(location.search).get('closet') === 'all') return html`<${Closet} review />`;

  if (me === undefined)
    return html`<div class="column"><${DesktopChrome} /><div class="empty">…</div></div>`;
  if (me === null)
    return html`<div class="column"><${DesktopChrome} />
      <${Landing} authMode=${health?.auth_mode ?? 'mock'} onSignedIn=${loadMe}
        next=${(() => {
          // carry the pocketed invite into the oauth next — the server claims it at the door,
          // and the /link handoff must not drop it or gated newcomers bounce off their own invite
          const inv = localStorage.getItem('vb:invite');
          const ok = inv && /^[A-Za-z0-9]+$/.test(inv);
          if (page === 'link') return location.pathname + location.search + (ok ? `&invite=${inv}` : '');
          return ok ? `/?invite=${inv}` : '';
        })()} />
    </div>`;
  if (page === 'link')
    return html`<div class="column"><${DesktopChrome} />
      <${LinkApprove} code=${linkCode} onDone=${() => {
        history.replaceState(null, '', '/');
        setPage('app');
      }} />
    </div>`;

  const inTable = table || tableQueued;

  return html`<div class="column">
    <${DesktopChrome} />
    <${StatusBar} me=${me} status=${status} sessions=${sessions} petMeta=${petMeta} onMe=${() => setTab('me')}
      onConnect=${connectAgents} onCopyPrompt=${copyAgentPrompt}
      bell=${html`<${NotificationBell} me=${me} onNavigate=${(n) => {
        if (n.tab) setTab(n.tab);
        if (n.conv_id) setThread({ conv_id: n.conv_id, name: n.name ?? 'chat' });
        if (n.username) setProfileUser(n.username);
        if (n.game_id) setActiveGame(Number(n.game_id)); // spectate deep-link from the drawer
      }} />`} />
    ${doneInfo &&
    html`<${DoneToast} info=${doneInfo}
      onPeek=${() => {
        setDoneInfo(null);
        setTab('garden'); // the seed just landed there
      }} />`}
    ${me && !me.hatched &&
    html`<${HatchCeremony} me=${me} onDone=${() => loadMe()} />`}
    ${me?.hatched && tour && html`<${Tour} onDone=${endTour} />`}
    ${evolution && html`<${EvolutionCeremony} me=${me} meta=${evolution} onDone=${() => setEvolution(null)} />`}
    <div class="main">
      ${tab === 'lobby' &&
      (inTable
        ? html`<div style="position:relative;flex:1;min-height:0;display:flex;flex-direction:column">
            ${!clubOff && html`<${ClubLobby} backdrop me=${me} roster=${roster} msgs=${msgs} />`}
            <${TableView} ws=${wsRef.current} me=${me} table=${table} tableMsgs=${tableMsgs}
              queued=${!table && tableQueued} onProfile=${setProfileUser} />
          </div>`
        : clubOff
          ? html`<div class="tablecall clickable" onClick=${() => {
                localStorage.setItem('vb:club:off', '0');
                setClubOff(false);
              }}>🪩 the club is dark — tap to turn the party back on</div>
              <${Lobby} ws=${wsRef.current} msgs=${msgs} online=${online} roster=${roster} me=${me} lastShip=${lastShip}
                tableCall=${tableCall} onProfile=${setProfileUser}
                onRoll=${(size) => {
                  wsRef.current?.send(JSON.stringify({ t: 'table_join', size }));
                  setTableQueued({ size, waiting: 1 });
                  setTableCall(null);
                }} />`
          : html`<${ClubLobby} ws=${wsRef.current} msgs=${msgs} online=${online} roster=${roster} me=${me}
              lastShip=${lastShip} tableCall=${tableCall} onProfile=${setProfileUser}
              onOff=${() => {
                localStorage.setItem('vb:club:off', '1');
                setClubOff(true);
              }}
              onRoll=${(size) => {
                wsRef.current?.send(JSON.stringify({ t: 'table_join', size }));
                setTableQueued({ size, waiting: 1 });
                setTableCall(null);
              }} />`)}
      ${tab === 'buddies' &&
      html`<${BuddiesTab} social=${social} me=${me} roster=${roster} ticketsVersion=${ticketsVersion}
        onProfile=${setProfileUser} onOpenDm=${openDm}
        reloadSocial=${reloadSocial} onToast=${showToast} />`}
      ${tab === 'chats' &&
      (thread
        ? html`<${Thread} convId=${thread.conv_id} convName=${thread.name} me=${me}
            conv=${social?.conversations.find((x) => x.conv_id === thread.conv_id)}
            feedEvents=${dmEvents} onBack=${() => setThread(null)} onProfile=${setProfileUser} onRead=${markConvRead}
            onLeft=${() => { setThread(null); reloadSocial(); }} onToast=${showToast} />`
        : html`<${ChatsTab} social=${social} me=${me} ticketsVersion=${ticketsVersion}
            onOpenThread=${openThread} onProfile=${setProfileUser} reloadSocial=${reloadSocial} onToast=${showToast} />`)}
      ${tab === 'play' &&
      (activeGame
        ? html`<${GameView} gameId=${activeGame} me=${me} ws=${wsRef.current} gameEvents=${gameEvents}
            onBack=${() => setActiveGame(null)} onRematch=${setActiveGame} />`
        : html`<${PlayTab} me=${me} social=${social} onOpenGame=${setActiveGame}
            onProfile=${setProfileUser} onToast=${showToast} version=${gamesVersion} />`)}
      ${tab === 'garden' && html`<${GardenTab} me=${me} gardenVersion=${gardenVersion} onToast=${showToast} />`}
      ${tab === 'me' && html`<${MeTab} me=${me} status=${status} sessions=${sessions} gardenVersion=${gardenVersion} onToast=${showToast} petMeta=${petMeta} onRefresh=${loadMe} onReplayTour=${() => setTour(true)} />`}
    </div>
    <div class="tabs">
      ${TABS.map((t) => {
        const dot =
          (t.id === 'chats' && social?.conversations.some((c) => c.unread)) ||
          (t.id === 'buddies' && ((social?.requests_in?.length ?? 0) > 0 || pendingTickets > 0));
        return html`<button class=${tab === t.id ? 'active' : ''} onClick=${() => setTab(t.id)}>
          <span class="glyph"><${TabIcon} name=${t.id} />${dot && html`<span class="unreaddot tabdot"></span>`}</span>${t.label}
        </button>`;
      })}
    </div>
    ${toast &&
    html`<div class="toast ${toast.onTap ? 'clickable' : ''}" style="z-index:90" onClick=${() => {
      toast.onTap?.();
      setToast(null);
    }}>${toast.text}</div>`}
    ${profileUser &&
    html`<${ProfileView} username=${profileUser} me=${me} onClose=${() => setProfileUser(null)}
      onOpenDm=${openDm} reloadSocial=${reloadSocial} />`}
  </div>`;
}

render(html`<${App} />`, document.getElementById('app'));
